import React, { Suspense, useEffect, useState } from "react";
import { useDarkMode } from "../context/DarkModeContext";

// Remotion-powered branded wait states, played LIVE via @remotion/player (no
// video files). Variants: "route" (plane orbiting the FK tile — generic
// loading) and "parse" (violet AI scan beam — extraction/AI waits).
//
// Design contract:
// - remotion stays out of the main bundle (lazy chunk); the static FK mark
//   renders instantly as its fallback.
// - prefers-reduced-motion gets the static mark, never the animation.
const PlayerCanvas = React.lazy(() => import("./PlayerCanvas"));

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches
  );
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return undefined;
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

// Instant, animation-free stand-in: the FK tile (route) / AI chip (parse).
function StaticMark({ variant, size }) {
  const isParse = variant === "parse";
  return (
    <svg viewBox="0 0 400 400" width={size} height={size} aria-hidden>
      <rect
        x={130}
        y={130}
        width={140}
        height={140}
        rx={32}
        fill={isParse ? "#8B5CF6" : "#2563EB"}
      />
      <text
        x={200}
        y={222}
        textAnchor="middle"
        fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
        fontWeight={800}
        fontSize={64}
        fill="#FFFFFF"
      >
        {isParse ? "✦" : "FK"}
      </text>
    </svg>
  );
}

/**
 * @param {object} props
 * @param {"route"|"parse"} [props.variant]
 * @param {number} [props.size] square edge in px
 * @param {string} [props.label] accessible label + optional caption below
 * @param {boolean} [props.showLabel] render the label as a visible caption
 */
function AnimatedLoader({ variant = "route", size = 128, label = "Loading", showLabel = false, extraProps }) {
  const { isDarkMode } = useDarkMode();
  const reduced = usePrefersReducedMotion();
  return (
    <div
      role="status"
      aria-label={label}
      className="flex flex-col items-center justify-center gap-2"
    >
      {reduced ? (
        <StaticMark variant={variant} size={size} />
      ) : (
        <Suspense fallback={<StaticMark variant={variant} size={size} />}>
          <PlayerCanvas variant={variant} size={size} dark={isDarkMode} extraProps={extraProps} />
        </Suspense>
      )}
      {showLabel && (
        <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
          {label}
        </p>
      )}
    </div>
  );
}

export default AnimatedLoader;
